import React, { useState } from "react";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  MagnifyingGlass, CheckCircle, Circle, Clock, XCircle, WhatsappLogo,
  Barcode, DeviceMobile, DownloadSimple, ChatCircleDots, Warning, Receipt,
  ArrowRight, CalendarBlank, Copy,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import { BRAND, waLink } from "@/lib/brand";

// ---------- helpers ----------
const stageStyle = (status) => {
  if (status === "done")    return { color: "text-emerald-600", bg: "bg-emerald-100 border-emerald-500", ring: "ring-emerald-500/25", icon: CheckCircle };
  if (status === "current") return { color: "text-orange-600",  bg: "bg-orange-100 border-orange-500",    ring: "ring-orange-500/25 animate-pulse", icon: Clock };
  return                          { color: "text-slate-400",   bg: "bg-slate-100 border-slate-300",     ring: "", icon: Circle };
};

const fmtDate = (iso) => {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    return d.toLocaleString("en-IN", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
  } catch { return iso.slice(0, 16).replace("T", " "); }
};

export default function PublicTrack() {
  const [mode, setMode] = useState("app");            // app | mobile
  const [appNumber, setAppNumber] = useState("");
  const [mobile, setMobile] = useState("");
  const [dob, setDob] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [selectedId, setSelectedId] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    if (mode === "app" && !appNumber.trim()) { toast.error("Enter Application Number"); return; }
    if (mode === "mobile" && !/^\d{10}$/.test(mobile.replace(/\D/g, "").slice(-10))) { toast.error("Enter a valid 10-digit mobile"); return; }
    setBusy(true); setResult(null); setSelectedId(null);
    try {
      const body = mode === "app"
        ? { app_number: appNumber.trim().toUpperCase(), dob: dob || "" }
        : { mobile };
      const { data } = await api.post("/public/track", body);
      setResult(data);
      if (data.found === 0) toast.info("No applications found");
      if (data.applications?.length === 1) setSelectedId(data.applications[0].id);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Track failed");
    } finally { setBusy(false); }
  };

  const selected = result?.applications?.find((a) => a.id === selectedId) || null;

  return (
    <div className="max-w-6xl mx-auto px-4 py-14" data-testid="public-track">
      <div className="text-center mb-8">
        <div className="text-[10px] uppercase tracking-[0.25em] font-semibold text-[#1e3a8a] mb-1.5">Customer Service Tracking</div>
        <h1 className="text-3xl sm:text-4xl font-bold text-slate-900" style={{ fontFamily: "var(--font-heading)" }}>Track your application</h1>
        <p className="text-slate-600 mt-2">Every application, every stage — transparent from submission to certificate delivery.</p>
      </div>

      {/* ---- Search card ---- */}
      <Card className="p-6 bg-white border-slate-200 shadow-sm mb-8" data-testid="track-search-card">
        {/* Mode toggle */}
        <div className="flex gap-1 mb-5 p-1 bg-slate-100 rounded-full w-fit" data-testid="track-mode-toggle">
          {[
            { id: "app", label: "By Application No.", icon: Barcode },
            { id: "mobile", label: "By Mobile", icon: DeviceMobile },
          ].map((m) => {
            const Ic = m.icon;
            return (
              <button
                key={m.id}
                onClick={() => setMode(m.id)}
                data-testid={`track-mode-${m.id}`}
                className={`inline-flex items-center gap-1.5 px-4 h-9 rounded-full text-xs font-semibold transition-colors ${mode === m.id ? "bg-white shadow-sm text-[#1e3a8a]" : "text-slate-600 hover:text-slate-900"}`}
              >
                <Ic size={12} /> {m.label}
              </button>
            );
          })}
        </div>

        <form onSubmit={submit} className="flex flex-wrap items-end gap-3" data-testid="track-form">
          {mode === "app" ? (
            <>
              <div className="flex-1 min-w-[260px]">
                <Label className="text-xs uppercase tracking-wider font-semibold text-slate-600 mb-1.5 block">Application Number *</Label>
                <Input value={appNumber} onChange={(e) => setAppNumber(e.target.value.toUpperCase())} placeholder="e.g. DS260812000123" className="h-11 font-mono" data-testid="track-appnum" />
              </div>
              <div className="w-40">
                <Label className="text-xs uppercase tracking-wider font-semibold text-slate-600 mb-1.5 block">DOB (optional)</Label>
                <Input type="date" value={dob} onChange={(e) => setDob(e.target.value)} className="h-11" data-testid="track-dob" />
              </div>
            </>
          ) : (
            <div className="flex-1 min-w-[260px]">
              <Label className="text-xs uppercase tracking-wider font-semibold text-slate-600 mb-1.5 block">Mobile Number *</Label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 font-mono text-sm">+91</span>
                <Input value={mobile} onChange={(e) => setMobile(e.target.value)} maxLength={15} className="pl-12 h-11" placeholder="98xxxxxxxx" data-testid="track-mobile" />
              </div>
            </div>
          )}
          <Button type="submit" disabled={busy} className="h-11 bg-[#1e3a8a] hover:bg-[#1e2f6b] text-white gap-1.5 px-6" data-testid="track-submit">
            <MagnifyingGlass size={14} /> {busy ? "Searching…" : "Track Status"}
          </Button>
        </form>
      </Card>

      {/* ---- Results ---- */}
      {result && result.found === 0 && (
        <div className="text-center py-14 text-slate-500 text-sm bg-white rounded-xl border border-slate-200" data-testid="track-empty">
          No applications found.<br />
          <a href="/apply" className="text-[#1e3a8a] underline mt-2 inline-block">Apply for a service</a>
          {" · "}
          <a href={waLink()} target="_blank" rel="noreferrer" className="text-emerald-600 underline">WhatsApp us</a>
        </div>
      )}

      {result && result.found > 1 && !selected && (
        <div className="space-y-3 mb-8" data-testid="track-list">
          <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold mb-3">Found {result.found} applications — pick one:</div>
          {result.applications.map((a) => (
            <button key={a.id} onClick={() => setSelectedId(a.id)} data-testid={`track-app-${a.id}`}
              className="w-full text-left p-4 bg-white border border-slate-200 rounded-xl hover:border-[#1e3a8a] hover:shadow-md transition-all flex items-center gap-4">
              <StagePill stage={a.stage} />
              <div className="flex-1">
                <div className="font-semibold text-slate-900">{a.service}</div>
                <div className="text-[11px] text-slate-500 mt-0.5">
                  <span className="font-mono">{a.app_number}</span> · Applied {String(a.created_at).slice(0, 10)}
                </div>
              </div>
              <ArrowRight size={14} className="text-slate-400" />
            </button>
          ))}
        </div>
      )}

      {selected && <TrackDetail app={selected} back={() => setSelectedId(null)} showBack={result.found > 1} />}
    </div>
  );
}

// ============================================================
// Timeline detail
// ============================================================
function TrackDetail({ app, back, showBack }) {
  const doneCount   = app.timeline.filter((t) => t.status === "done").length;
  const percent     = Math.round((doneCount / app.timeline.length) * 100);
  const rejected    = app.rejected;
  const copyAppNum = async () => {
    try { await navigator.clipboard.writeText(app.app_number); toast.success("Copied"); } catch {}
  };

  return (
    <div className="grid lg:grid-cols-3 gap-6" data-testid="track-detail">
      {/* Left — timeline */}
      <div className="lg:col-span-2 space-y-6">
        <Card className="p-6 bg-white border-slate-200 shadow-sm">
          <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
            <div>
              <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">Application</div>
              <div className="flex items-center gap-2 mt-0.5">
                <div className="text-xl font-bold text-slate-900 font-mono" data-testid="track-detail-appnum">{app.app_number}</div>
                <button onClick={copyAppNum} className="p-1 text-slate-400 hover:text-[#1e3a8a]" title="Copy"><Copy size={12} /></button>
              </div>
              <div className="text-sm text-slate-600 mt-1">{app.service}{app.department ? ` · ${app.department}` : ""}</div>
            </div>
            <div className="text-right">
              <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">Progress</div>
              <div className={`text-3xl font-bold ${rejected ? "text-red-600" : "text-emerald-600"}`}>{percent}%</div>
            </div>
          </div>

          {/* Progress bar */}
          <div className="h-2 rounded-full bg-slate-100 overflow-hidden mb-6">
            <div className={`h-full rounded-full transition-all ${rejected ? "bg-red-500" : "bg-gradient-to-r from-emerald-400 to-emerald-600"}`} style={{ width: `${percent}%` }} />
          </div>

          {rejected && (
            <div className="p-3 rounded-lg bg-red-50 border border-red-200 flex items-start gap-2 mb-4" data-testid="track-rejected">
              <XCircle size={16} weight="fill" className="text-red-600 shrink-0 mt-0.5" />
              <div className="text-sm text-red-800">
                <div className="font-semibold">Application rejected</div>
                <div className="text-xs mt-0.5">Please WhatsApp our team for next steps.</div>
              </div>
            </div>
          )}

          {/* Timeline steps */}
          <div className="space-y-1" data-testid="track-timeline">
            {app.timeline.map((t, i) => {
              const s = stageStyle(t.status);
              const Ic = s.icon;
              return (
                <div key={i} className="flex gap-3 group" data-testid={`timeline-step-${i}`}>
                  <div className="flex flex-col items-center">
                    <div className={`w-8 h-8 rounded-full border-2 flex items-center justify-center ${s.bg} ${s.ring ? `ring-4 ${s.ring}` : ""}`}>
                      <Ic size={14} weight={t.status === "done" ? "fill" : "regular"} className={s.color} />
                    </div>
                    {i < app.timeline.length - 1 && (
                      <div className={`w-0.5 flex-1 my-1 ${t.status === "done" ? "bg-emerald-300" : "bg-slate-200"}`} style={{ minHeight: 20 }} />
                    )}
                  </div>
                  <div className="flex-1 pb-4">
                    <div className={`text-sm font-semibold ${t.status === "current" ? "text-orange-700" : t.status === "done" ? "text-slate-900" : "text-slate-400"}`}>
                      {t.stage}
                      {t.status === "current" && <span className="ml-2 text-[9px] font-bold uppercase tracking-widest px-1.5 py-0.5 rounded bg-orange-100 text-orange-700">In progress</span>}
                    </div>
                    {t.at && <div className="text-[11px] text-slate-500 mt-0.5">{fmtDate(t.at)}</div>}
                    {t.note && <div className="text-xs text-slate-600 mt-0.5 italic">"{t.note}"</div>}
                  </div>
                </div>
              );
            })}
          </div>
        </Card>

        {/* Customer actions */}
        <Card className="p-5 bg-white border-slate-200" data-testid="track-actions">
          <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold mb-3">Customer Actions</div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
            <ActionButton icon={Receipt}       label="Download Receipt"  onClick={() => toast.info("Please visit branch to collect your receipt")} testid="act-receipt" />
            <ActionButton icon={DownloadSimple} label="Certificate"       onClick={() => toast.info("Certificate will be available once your application is Approved")} testid="act-cert" />
            <ActionButton icon={ChatCircleDots} label="Chat on WhatsApp" href={waLink(`Hi, tracking application ${app.app_number} — ${app.service}. Current stage: ${app.stage}.`)} testid="act-wa" />
            <ActionButton icon={CalendarBlank}  label="Book Appointment" onClick={() => toast.info("Booking appointments launches soon. WhatsApp us to schedule.")} testid="act-book" />
          </div>
        </Card>
      </div>

      {/* Right — details card */}
      <div className="space-y-4">
        {showBack && (
          <button onClick={back} className="text-xs text-[#1e3a8a] font-semibold hover:underline" data-testid="track-back">← Back to list</button>
        )}
        <Card className="p-5 bg-white border-slate-200" data-testid="track-info-card">
          <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold mb-3">Application Details</div>
          <dl className="space-y-2.5 text-sm">
            <Detail label="Current Stage" value={<StagePill stage={app.stage} inline />} />
            <Detail label="Priority" value={<span className={`text-xs font-semibold px-2 py-0.5 rounded ${app.priority === "Emergency" ? "bg-red-100 text-red-800" : app.priority === "Urgent" ? "bg-amber-100 text-amber-800" : "bg-slate-100 text-slate-700"}`}>{app.priority || "Normal"}</span>} />
            <Detail label="Payment" value={<span className={`text-xs font-semibold ${app.payment_status === "Paid" ? "text-emerald-700" : "text-amber-700"}`}>{app.payment_status}</span>} />
            {app.total > 0 && <Detail label="Amount" value={<span className="font-mono font-semibold">₹{Number(app.total).toLocaleString("en-IN")}</span>} />}
            {app.balance_due > 0 && <Detail label="Balance Due" value={<span className="font-mono font-semibold text-amber-700">₹{Number(app.balance_due).toLocaleString("en-IN")}</span>} />}
            {app.portal_app_no && <Detail label="Govt Ref" value={<span className="font-mono text-xs">{app.portal_app_no}</span>} />}
            {app.expected_completion && <Detail label="Expected" value={fmtDate(app.expected_completion).slice(0, 12)} />}
            <Detail label="Applied" value={fmtDate(app.created_at)} />
            {app.stage_updated_at && <Detail label="Last Update" value={fmtDate(app.stage_updated_at)} />}
          </dl>
        </Card>

        <Card className="p-5 bg-gradient-to-br from-emerald-500 to-emerald-600 text-white border-emerald-500">
          <div className="flex items-center gap-2 mb-2">
            <WhatsappLogo size={22} weight="fill" />
            <div className="font-semibold">Need help?</div>
          </div>
          <p className="text-sm text-emerald-50 mb-3">Reply on WhatsApp with your Application No. and we'll help you in 5 minutes.</p>
          <a href={waLink(`Hi, I have a question about application ${app.app_number}.`)} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 px-4 h-10 rounded-full bg-white text-emerald-700 text-sm font-semibold hover:-translate-y-0.5 transition-transform">
            <WhatsappLogo size={14} weight="fill" /> Chat on WhatsApp
          </a>
        </Card>

        {app.balance_due > 0 && (
          <Card className="p-4 bg-amber-50 border-amber-200" data-testid="track-payment-due">
            <div className="flex items-center gap-2 mb-1">
              <Warning size={16} weight="fill" className="text-amber-600" />
              <div className="font-semibold text-amber-900 text-sm">Payment pending</div>
            </div>
            <p className="text-xs text-amber-800 mb-2">Balance ₹{Number(app.balance_due).toLocaleString("en-IN")} pending. Pay at branch or WhatsApp us.</p>
          </Card>
        )}
      </div>
    </div>
  );
}

function StagePill({ stage, inline = false }) {
  const cls = inline ? "text-xs font-semibold px-2 py-0.5 rounded" : "text-xs font-semibold px-2.5 py-1 rounded-full";
  const style = /Reject/i.test(stage) ? "bg-red-100 text-red-800"
              : /Completed|Delivered/i.test(stage) ? "bg-emerald-100 text-emerald-800"
              : /Approved|Certificate/i.test(stage) ? "bg-blue-100 text-blue-800"
              : /Processing|Submitted/i.test(stage) ? "bg-amber-100 text-amber-800"
              : "bg-slate-100 text-slate-800";
  return <span className={`${cls} ${style}`}>{stage}</span>;
}

function Detail({ label, value }) {
  return (
    <div className="flex items-center justify-between gap-3">
      <dt className="text-[11px] uppercase tracking-wider font-semibold text-slate-500">{label}</dt>
      <dd className="text-sm text-slate-900 text-right">{value}</dd>
    </div>
  );
}

function ActionButton({ icon: Ic, label, onClick, href, testid }) {
  const cls = "flex flex-col items-center justify-center gap-1.5 p-3 rounded-xl border border-slate-200 bg-white hover:border-[#1e3a8a] hover:bg-slate-50 transition-all";
  if (href) {
    return (
      <a href={href} target="_blank" rel="noreferrer" className={cls} data-testid={testid}>
        <Ic size={20} className="text-[#1e3a8a]" weight="duotone" />
        <span className="text-[11px] font-medium text-slate-700 text-center leading-tight">{label}</span>
      </a>
    );
  }
  return (
    <button onClick={onClick} className={cls} data-testid={testid}>
      <Ic size={20} className="text-[#1e3a8a]" weight="duotone" />
      <span className="text-[11px] font-medium text-slate-700 text-center leading-tight">{label}</span>
    </button>
  );
}
